/**
 * AssignEmailModal — Assign a synced email activity to an opportunity
 */

import React, { useState, useMemo } from 'react';
import {
  Mail,
  Search,
  Target,
  Plus,
  ArrowRight,
  X,
  Paperclip,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface Activity {
  id: string;
  type: string;
  title: string;
  description?: string;
  direction?: 'inbound' | 'outbound';
  activityDate?: string;
  opportunityId?: string;
  metadata?: {
    from_email?: string;
    to_emails?: string[];
    cc_emails?: string[];
    provider?: string;
    folder?: string;
    importance?: string;
    has_attachments?: boolean;
  };
}

interface Opportunity {
  id: string;
  name: string;
  company: string;
  stage: string;
  amount: number;
}

interface AssignEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: Activity | null;
  opportunities: Opportunity[];
  onAssign: (activityId: string, opportunityId: string) => void;
  onCreateOpportunity: (activity: Activity) => void;
}

export function AssignEmailModal({ isOpen, onClose, activity, opportunities, onAssign, onCreateOpportunity }: AssignEmailModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOppId, setSelectedOppId] = useState<string>('');

  const filteredOpps = useMemo(() => {
    if (!searchQuery) return opportunities;
    const q = searchQuery.toLowerCase();
    return opportunities.filter(o =>
      o.name.toLowerCase().includes(q) ||
      o.company.toLowerCase().includes(q) ||
      o.stage.toLowerCase().includes(q)
    );
  }, [opportunities, searchQuery]);

  const handleSave = () => {
    if (!activity || !selectedOppId) return;
    onAssign(activity.id, selectedOppId);
    setSelectedOppId('');
    setSearchQuery('');
  };

  const handleClose = () => {
    setSelectedOppId('');
    setSearchQuery('');
    onClose();
  };

  if (!activity) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Assign Email to Opportunity
          </DialogTitle>
        </DialogHeader>

        {/* Email Summary */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-medium text-gray-900 text-sm truncate">{activity.title}</h4>
          <div className="mt-2 space-y-1 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                activity.direction === 'outbound'
                  ? 'bg-blue-50 text-blue-600'
                  : 'bg-green-50 text-green-600'
              }`}>
                {activity.direction === 'outbound' ? '→ Sent' : '← Received'}
              </span>
              {activity.activityDate && (
                <span>{new Date(activity.activityDate).toLocaleDateString()}</span>
              )}
              {activity.metadata?.has_attachments && (
                <Paperclip className="h-3 w-3 text-gray-400" />
              )}
            </div>
            {activity.metadata?.from_email && (
              <p><span className="text-gray-400">From:</span> {activity.metadata.from_email}</p>
            )}
            {activity.metadata?.to_emails && activity.metadata.to_emails.length > 0 && (
              <p className="truncate"><span className="text-gray-400">To:</span> {activity.metadata.to_emails.join(', ')}</p>
            )}
            {activity.metadata?.cc_emails && activity.metadata.cc_emails.length > 0 && (
              <p className="truncate"><span className="text-gray-400">Cc:</span> {activity.metadata.cc_emails.join(', ')}</p>
            )}
          </div>
          {activity.description && (
            <p className="mt-2 text-xs text-gray-500 line-clamp-2">{activity.description}</p>
          )}
        </div>

        {/* Search Opportunities */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assign to Opportunity
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search opportunities..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {filteredOpps.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {opportunities.length === 0 ? 'No opportunities yet' : 'No matching opportunities'}
              </div>
            ) : (
              filteredOpps.map(opp => (
                <button
                  key={opp.id}
                  onClick={() => setSelectedOppId(opp.id)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-0 hover:bg-blue-50 transition-colors ${
                    selectedOppId === opp.id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{opp.name}</p>
                      <p className="text-xs text-gray-500">{opp.company} · {opp.stage}</p>
                    </div>
                    {selectedOppId === opp.id && (
                      <Target className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Create New Opportunity */}
        <button
          onClick={() => onCreateOpportunity(activity)}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 border border-blue-200"
        >
          <Plus className="h-4 w-4" />
          Create New Opportunity from this Email
          <ArrowRight className="h-3 w-3 ml-auto" />
        </button>

        <DialogFooter>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedOppId}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Assign to Opportunity
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
